import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, FileText, Search, X, DollarSign, Banknote, Smartphone, Building2, CreditCard, Plus, Trash2, Zap } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { paymentsReceivedAPI, partiesAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'upi', label: 'UPI', icon: Smartphone },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: Building2 },
  { value: 'card', label: 'Card', icon: CreditCard },
  { value: 'cheque', label: 'Cheque', icon: FileText },
  { value: 'wallet', label: 'Wallet', icon: DollarSign },
  { value: 'other', label: 'Other', icon: DollarSign },
];

// Hardcoded classes -- dynamic `text-${color}-500` gets purged by Tailwind at build time
const MODE_STYLES = {
  cash:          { border: 'border-emerald-500', bg: 'bg-emerald-50', icon: 'text-emerald-600', label: 'text-emerald-700' },
  upi:           { border: 'border-purple-500',  bg: 'bg-purple-50',  icon: 'text-purple-600',  label: 'text-purple-700'  },
  bank_transfer: { border: 'border-blue-500',    bg: 'bg-blue-50',    icon: 'text-blue-600',    label: 'text-blue-700'    },
  card:          { border: 'border-amber-500',   bg: 'bg-amber-50',   icon: 'text-amber-600',   label: 'text-amber-700'   },
  cheque:        { border: 'border-slate-500',   bg: 'bg-slate-50',   icon: 'text-slate-600',   label: 'text-slate-700'   },
  wallet:        { border: 'border-cyan-500',    bg: 'bg-cyan-50',    icon: 'text-cyan-600',    label: 'text-cyan-700'    },
  other:         { border: 'border-gray-500',    bg: 'bg-gray-50',    icon: 'text-gray-600',    label: 'text-gray-700'    },
};

const RecordPaymentPage = () => {
  const { businessId, id: paymentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCustomerId = searchParams.get('customer');
  const { activeBusiness } = useBusiness();
  const isEditing = !!paymentId;
  const bid = businessId || activeBusiness?.id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const customerSearchRef = useRef(null);

  const [formData, setFormData] = useState({
    customer_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    amount_received: '',
    payment_mode: 'cash',
    payment_subtype: '',
    transaction_reference: '',
    deposit_to_account: 'Cash',
    notes: '',
    // Mode-specific
    upi_app: '',
    bank_name: '',
    cheque_number: '',
    cheque_date: '',
    card_last4: '',
    card_type: ''
  });

  const [allocations, setAllocations] = useState([]);

  useEffect(() => {
    if (bid) {
      fetchCustomers();
      if (isEditing) fetchPayment();
    }
  }, [bid, paymentId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customerSearchRef.current && !customerSearchRef.current.contains(e.target)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      fetchOpenInvoices(selectedCustomer.party_id || selectedCustomer.id);
    }
  }, [selectedCustomer]);

  const fetchPayment = async () => {
    setLoading(true);
    try {
      const response = await paymentsReceivedAPI.get(paymentId);
      const data = response.data;
      if (data) {
        setFormData({
          customer_id: data.customer_id || data.party_id || '',
          payment_date: data.payment_date || new Date().toISOString().split('T')[0],
          amount_received: data.amount_received || '',
          payment_mode: data.payment_mode || 'cash',
          payment_subtype: data.payment_subtype || '',
          transaction_reference: data.transaction_reference || data.transaction_id || '',
          deposit_to_account: data.deposit_to_account || 'Cash',
          notes: data.notes || '',
          upi_app: data.upi_app || '',
          bank_name: data.bank_name || '',
          cheque_number: data.cheque_number || '',
          cheque_date: data.cheque_date || '',
          card_last4: data.card_last4 || '',
          card_type: data.card_type || ''
        });
        setAllocations(data.allocations || data.invoices_allocated || []);
        setCustomerSearch(data.customer_name || data.party_name || '');
        setSelectedCustomer({
          id: data.customer_id || data.party_id,
          name: data.customer_name || data.party_name,
          phone: data.customer_phone || data.party_phone
        });
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error loading payment'));
    } finally { setLoading(false); }
  };

  const fetchCustomers = async () => {
    try {
      const response = await partiesAPI.list({ type: 'customer' });
      const data = response.data;
      setCustomers(data.parties || data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load customers'));
    }
  };

  const fetchOpenInvoices = async (customerId) => {
    try {
      const response = await paymentsReceivedAPI.openInvoices(customerId);
      setOpenInvoices(response.data.invoices || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load open invoices'));
    }
  };

  // Pre-select customer from query param (e.g. from My Retailers page)
  useEffect(() => {
    if (!preselectedCustomerId || isEditing || selectedCustomer) return;
    const preselectCustomer = async () => {
      const match = customers.find(c => (c.party_id || c.id) === preselectedCustomerId);
      if (match) {
        handleCustomerSelect(match);
        return;
      }
      // Not in list (could be supplier/different type) -- fetch directly by ID
      if (customers.length > 0) {
        try {
          const { data } = await partiesAPI.get(preselectedCustomerId);
          const party = data.party || data;
          if (party && party.name) {
            handleCustomerSelect({ id: party.id, party_id: party.id, name: party.name, phone: party.phone });
          }
        } catch (e) {
          console.warn('Could not fetch preselected customer:', e);
        }
      }
    };
    preselectCustomer();
  }, [preselectedCustomerId, customers]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
    setFormData(prev => ({ ...prev, customer_id: customer.party_id || customer.id }));
    setAllocations([]);
  };

  const handleModeChange = (mode) => {
    setFormData(prev => ({
      ...prev,
      payment_mode: mode,
      // Reset mode-specific fields
      upi_app: '', bank_name: '', cheque_number: '', cheque_date: '', card_last4: '', card_type: ''
    }));
  };

  // Allocation handlers
  const addAllocation = (invoice) => {
    if (allocations.find(a => a.invoice_id === invoice.id)) return;
    setAllocations(prev => [...prev, {
      invoice_id: invoice.id,
      invoice_number: invoice.invoice_number,
      invoice_date: invoice.invoice_date,
      invoice_total: invoice.grand_total,
      allocated_amount: Math.min(invoice.balance_due, getRemainingAmount())
    }]);
  };

  const removeAllocation = (index) => {
    setAllocations(prev => prev.filter((_, i) => i !== index));
  };

  const updateAllocationAmount = (index, amount) => {
    setAllocations(prev => {
      const newAllocs = [...prev];
      const invoice = openInvoices.find(inv => inv.id === newAllocs[index].invoice_id);
      const maxAmount = invoice?.balance_due || 0;
      newAllocs[index].allocated_amount = Math.min(amount, maxAmount);
      return newAllocs;
    });
  };

  const getTotalAllocated = () => allocations.reduce((sum, a) => sum + (parseFloat(a.allocated_amount) || 0), 0);
  const getRemainingAmount = () => (parseFloat(formData.amount_received) || 0) - getTotalAllocated();

  // Auto-allocate to oldest invoices first
  const autoAllocateOldest = () => {
    let remaining = parseFloat(formData.amount_received) || 0;
    const newAllocs = [];
    
    const sortedInvoices = [...openInvoices].sort((a, b) => new Date(a.invoice_date) - new Date(b.invoice_date));
    
    for (const inv of sortedInvoices) {
      if (remaining <= 0) break;
      const allocAmount = Math.min(inv.balance_due, remaining);
      newAllocs.push({
        invoice_id: inv.id,
        invoice_number: inv.invoice_number,
        invoice_date: inv.invoice_date,
        invoice_total: inv.grand_total,
        allocated_amount: allocAmount
      });
      remaining -= allocAmount;
    }
    
    setAllocations(newAllocs);
  };

  // Auto-allocate by due date (most urgent first)
  const autoAllocateDueDate = () => {
    let remaining = parseFloat(formData.amount_received) || 0;
    const newAllocs = [];
    
    const sortedInvoices = [...openInvoices].sort((a, b) => new Date(a.due_date || a.invoice_date) - new Date(b.due_date || b.invoice_date));
    
    for (const inv of sortedInvoices) {
      if (remaining <= 0) break;
      const allocAmount = Math.min(inv.balance_due, remaining);
      newAllocs.push({
        invoice_id: inv.id,
        invoice_number: inv.invoice_number,
        invoice_date: inv.invoice_date,
        invoice_total: inv.grand_total,
        allocated_amount: allocAmount
      });
      remaining -= allocAmount;
    }
    
    setAllocations(newAllocs);
  };

  const handleSave = async (asDraft = false) => {
    if (!formData.customer_id) { toast.error('Please select a customer'); return; }
    if (!formData.amount_received || parseFloat(formData.amount_received) <= 0) { toast.error('Enter a valid amount'); return; }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        amount_received: parseFloat(formData.amount_received),
        customer_name: selectedCustomer?.name || '',
        customer_phone: selectedCustomer?.mobile || selectedCustomer?.phone || '',
        allocations: allocations,
        save_as_draft: asDraft
      };

      let response;
      if (isEditing) {
        response = await paymentsReceivedAPI.update(paymentId, payload);
      } else {
        response = await paymentsReceivedAPI.create(payload);
      }
      const data = response.data;
      toast.success(asDraft ? 'Saved as draft' : 'Payment recorded');
      navigate(`/business/${bid}/payments-received/${data.payment?.id || paymentId}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error saving payment'));
    } finally { setSaving(false); }
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.mobile?.includes(customerSearch) ||
    c.email?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const unallocatedInvoices = openInvoices.filter(inv => !allocations.find(a => a.invoice_id === inv.id));

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F0F2F5]">
        <div className="w-8 h-8 border-[3px] border-slate-200 border-t-emerald-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F0F2F5]">
      {/* HEADER */}
      <div className="flex-shrink-0 bg-[#F0F2F5] px-6 py-5">
        <div className="max-w-[1400px] mx-auto flex items-start justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-emerald-600">
              {isEditing ? 'Edit Payment' : 'Record Payment'}
            </h1>
            <p className="text-[13px] text-slate-500 mt-1">Record customer payment received</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/business/${bid}/payments-received`)}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-slate-600 hover:text-slate-800 text-[13px] font-medium">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={() => handleSave(true)} disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-[13px] font-medium shadow-sm">
              <FileText className="w-4 h-4" /> Save Draft
            </button>
            <button onClick={() => handleSave(false)} disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-[13px] font-medium shadow-sm">
              <Check className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Payment'}
            </button>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - 8 cols */}
            <div className="col-span-8 space-y-5">
              
              {/* CUSTOMER Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <Search className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Customer</h2>
                </div>
                
                <div className="relative" ref={customerSearchRef}>
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type="text" value={customerSearch}
                    onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none"
                    placeholder="Search customer by name, mobile, email..."
                  />
                  
                  {showCustomerDropdown && filteredCustomers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredCustomers.slice(0, 8).map(customer => {
                        const contact = [customer.mobile, customer.email].filter(Boolean).join(' • ');
                        return (
                          <div key={customer.party_id || customer.id}
                            onClick={() => handleCustomerSelect(customer)}
                            className="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[13px] font-medium text-slate-800 truncate">{customer.name}</p>
                              {contact && <p className="text-[11px] text-slate-500 shrink-0 truncate">{contact}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedCustomer && (
                  <div className="mt-4 p-3 bg-emerald-50/50 rounded-lg border border-emerald-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[13px] font-medium text-slate-800">{selectedCustomer.name}</p>
                        <p className="text-[11px] text-slate-500">{selectedCustomer.mobile || selectedCustomer.phone}</p>
                      </div>
                      <button onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); setFormData(prev => ({ ...prev, customer_id: '' })); setAllocations([]); setOpenInvoices([]); }}
                        className="text-slate-400 hover:text-slate-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* PAYMENT MODE Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Payment Mode</h2>
                </div>
                
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {PAYMENT_MODES.slice(0, 4).map(mode => {
                    const Icon = mode.icon;
                    const isSelected = formData.payment_mode === mode.value;
                    const s = MODE_STYLES[mode.value] || MODE_STYLES.other;
                    return (
                      <button key={mode.value}
                        onClick={() => handleModeChange(mode.value)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          isSelected ? `${s.border} ${s.bg}` : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Icon className={`w-6 h-6 ${isSelected ? s.icon : 'text-slate-400'}`} />
                        <span className={`text-[12px] font-medium ${isSelected ? s.label : 'text-slate-600'}`}>{mode.label}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {PAYMENT_MODES.slice(4).map(mode => {
                    const Icon = mode.icon;
                    const isSelected = formData.payment_mode === mode.value;
                    const s = MODE_STYLES[mode.value] || MODE_STYLES.other;
                    return (
                      <button key={mode.value}
                        onClick={() => handleModeChange(mode.value)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          isSelected ? `${s.border} ${s.bg}` : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Icon className={`w-6 h-6 ${isSelected ? s.icon : 'text-slate-400'}`} />
                        <span className={`text-[12px] font-medium ${isSelected ? s.label : 'text-slate-600'}`}>{mode.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Mode-specific fields */}
                <div className="mt-4 pt-4 border-t border-slate-100">
                  {formData.payment_mode === 'upi' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[12px] text-slate-500 mb-1.5">UPI Reference / UTR</label>
                        <input type="text" value={formData.transaction_reference}
                          onChange={(e) => setFormData(prev => ({ ...prev, transaction_reference: e.target.value }))}
                          className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-emerald-500"
                          placeholder="UPI transaction ID"
                        />
                      </div>
                      <div>
                        <label className="block text-[12px] text-slate-500 mb-1.5">UPI App</label>
                        <select value={formData.upi_app}
                          onChange={(e) => setFormData(prev => ({ ...prev, upi_app: e.target.value }))}
                          className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-emerald-500">
                          <option value="">Select App</option>
                          <option value="gpay">Google Pay</option>
                          <option value="phonepe">PhonePe</option>
                          <option value="paytm">Paytm</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                  )}
                  {formData.payment_mode === 'bank_transfer' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[12px] text-slate-500 mb-1.5">UTR / Reference</label>
                        <input type="text" value={formData.transaction_reference}
                          onChange={(e) => setFormData(prev => ({ ...prev, transaction_reference: e.target.value }))}
                          className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-emerald-500"
                          placeholder="Bank transaction reference"
                        />
                      </div>
                      <div>
                        <label className="block text-[12px] text-slate-500 mb-1.5">Bank Name</label>
                        <input type="text" value={formData.bank_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, bank_name: e.target.value }))}
                          className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-emerald-500"
                          placeholder="Bank name"
                        />
                      </div>
                    </div>
                  )}
                  {formData.payment_mode === 'cheque' && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-[12px] text-slate-500 mb-1.5">Cheque Number</label>
                        <input type="text" value={formData.cheque_number}
                          onChange={(e) => setFormData(prev => ({ ...prev, cheque_number: e.target.value }))}
                          className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-[12px] text-slate-500 mb-1.5">Cheque Date</label>
                        <input type="date" value={formData.cheque_date}
                          onChange={(e) => setFormData(prev => ({ ...prev, cheque_date: e.target.value }))}
                          className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-[12px] text-slate-500 mb-1.5">Bank</label>
                        <input type="text" value={formData.bank_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, bank_name: e.target.value }))}
                          className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-emerald-500"
                        />
                      </div>
                    </div>
                  )}
                  {formData.payment_mode === 'card' && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-[12px] text-slate-500 mb-1.5">Auth Code / Reference</label>
                        <input type="text" value={formData.transaction_reference}
                          onChange={(e) => setFormData(prev => ({ ...prev, transaction_reference: e.target.value }))}
                          className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-[12px] text-slate-500 mb-1.5">Card Last 4 Digits</label>
                        <input type="text" value={formData.card_last4} maxLength={4}
                          onChange={(e) => setFormData(prev => ({ ...prev, card_last4: e.target.value }))}
                          className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-emerald-500"
                          placeholder="XXXX"
                        />
                      </div>
                      <div>
                        <label className="block text-[12px] text-slate-500 mb-1.5">Card Type</label>
                        <select value={formData.card_type}
                          onChange={(e) => setFormData(prev => ({ ...prev, card_type: e.target.value }))}
                          className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-emerald-500">
                          <option value="">Select</option>
                          <option value="visa">Visa</option>
                          <option value="mastercard">Mastercard</option>
                          <option value="amex">Amex</option>
                          <option value="rupay">RuPay</option>
                        </select>
                      </div>
                    </div>
                  )}
                  {formData.payment_mode === 'cash' && (
                    <div>
                      <label className="block text-[12px] text-slate-500 mb-1.5">Reference (optional)</label>
                      <input type="text" value={formData.transaction_reference}
                        onChange={(e) => setFormData(prev => ({ ...prev, transaction_reference: e.target.value }))}
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-emerald-500"
                        placeholder="Internal reference or note"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* INVOICE ALLOCATION Card */}
              {selectedCustomer && (
                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-blue-600" />
                      </div>
                      <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Allocate to Invoices</h2>
                    </div>
                    {openInvoices.length > 0 && formData.amount_received && (
                      <div className="flex items-center gap-2">
                        <button onClick={autoAllocateOldest}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-[11px] font-medium hover:bg-blue-100">
                          <Zap className="w-3 h-3" /> Auto (Oldest First)
                        </button>
                        <button onClick={autoAllocateDueDate}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-600 rounded-lg text-[11px] font-medium hover:bg-amber-100">
                          <Zap className="w-3 h-3" /> Auto (Due Date)
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Current Allocations */}
                  {allocations.length > 0 && (
                    <div className="mb-4">
                      <p className="text-[11px] text-slate-500 uppercase font-medium mb-2">Allocated Invoices</p>
                      <div className="space-y-2">
                        {allocations.map((alloc, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 bg-emerald-50/50 rounded-lg border border-emerald-100">
                            <div className="flex-1">
                              <p className="text-[13px] font-medium text-slate-800">{alloc.invoice_number}</p>
                              <p className="text-[11px] text-slate-500">Total: ₹{(alloc.invoice_total || 0).toLocaleString('en-IN')}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] text-slate-500">Allocate:</span>
                              <input type="number" value={alloc.allocated_amount}
                                onChange={(e) => updateAllocationAmount(index, parseFloat(e.target.value) || 0)}
                                className="w-28 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[13px] text-right focus:outline-none focus:border-emerald-500"
                                min="0"
                              />
                            </div>
                            <button onClick={() => removeAllocation(index)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Available Invoices */}
                  {unallocatedInvoices.length > 0 ? (
                    <div>
                      <p className="text-[11px] text-slate-500 uppercase font-medium mb-2">Open Invoices</p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {unallocatedInvoices.map(inv => (
                          <div key={inv.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                            onClick={() => addAllocation(inv)}>
                            <div>
                              <p className="text-[13px] font-medium text-slate-800">{inv.invoice_number}</p>
                              <p className="text-[11px] text-slate-500">
                                {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-IN') : ''} • 
                                Due: ₹{(inv.balance_due || 0).toLocaleString('en-IN')}
                              </p>
                            </div>
                            <button className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded">
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : selectedCustomer && (
                    <div className="text-center py-6 text-slate-500">
                      <p className="text-[13px]">No open invoices for this customer</p>
                      <p className="text-[12px] text-amber-600 mt-1">Payment will be recorded as advance</p>
                    </div>
                  )}
                </div>
              )}

              {/* NOTES Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide mb-4">Notes</h2>
                <textarea value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none resize-none"
                  rows={3} placeholder="Add notes about this payment..."
                />
              </div>
            </div>

            {/* RIGHT COLUMN - 4 cols */}
            <div className="col-span-4 space-y-5">
              
              {/* PAYMENT DETAILS Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Payment Details</h2>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Payment Date</label>
                    <input type="date" value={formData.payment_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, payment_date: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Amount Received *</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                      <input type="number" value={formData.amount_received}
                        onChange={(e) => setFormData(prev => ({ ...prev, amount_received: e.target.value }))}
                        className="w-full pl-8 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-[15px] font-medium focus:outline-none focus:border-emerald-500"
                        placeholder="0.00" min="0" step="0.01"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Deposit To</label>
                    <select value={formData.deposit_to_account}
                      onChange={(e) => setFormData(prev => ({ ...prev, deposit_to_account: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-emerald-500">
                      <option value="Cash">Cash</option>
                      <option value="Bank">Bank</option>
                      <option value="Petty Cash">Petty Cash</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* SUMMARY Card */}
              <div className="bg-amber-50 rounded-xl p-5 shadow-sm border border-amber-200/60">
                <h3 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide mb-4">Allocation Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Amount Received</span>
                    <span className="text-slate-800 font-medium">₹{(parseFloat(formData.amount_received) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Allocated</span>
                    <span className="text-emerald-600 font-medium">₹{getTotalAllocated().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  <div className="border-t border-amber-200 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-800">Unallocated</span>
                      <span className={`text-[18px] font-bold ${getRemainingAmount() > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        ₹{getRemainingAmount().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    {getRemainingAmount() > 0 && (
                      <p className="text-[11px] text-amber-600 mt-2">Will be recorded as advance payment</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecordPaymentPage;
